import { useLens } from "@hookform/lenses";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Typography,
  Box,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useState, ReactElement, useEffect } from "react";
import { useForm } from "react-hook-form";

import {
  ImportOwnedPhoneNumberMutation,
  ImportOwnedPhoneNumberMutationVariables,
  PhoneNumberMessageClass,
  ProviderName,
} from "~frontend/__generated__/graphql";
import { MessageClassDisplayName } from "~frontend/components/MessageClassDisplayName";
import SubmitButton from "~frontend/components/forms/SubmitButton";
import {
  RhfErrorBanner,
  RhfSelect,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import { RhfCheckboxField } from "~frontend/components/forms/react-hook-form-components/RhfCheckboxField";
import { IMPORT_OWNED_PHONE_NUMBER } from "~frontend/graphql/ownedPhoneNumber";
import { GET_SUBACCOUNT_ADMIN_FIELDS } from "~frontend/graphql/subaccounts";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useNonCacheClearingApolloClient } from "~frontend/providers/NonCacheClearingApolloClientProvider";
import {
  useSubaccount,
  useSubaccountAdminConfig,
} from "~frontend/providers/SubaccountProvider";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import { PHONE_NUMBER_REFETCH_QUERIES } from ".";
import {
  DEFAULT_SHORT_CODE_CONFIG_FORM_VALUES,
  ShortCodeConfigForm,
  shortCodeConfigFormValuesToConfig,
  shortCodeConfigToFormValues,
} from "./ShortCodeConfigForm";
import {
  ImportOwnedPhoneNumberFormSchema,
  SHORT_CODE_CONFIG_KEYS,
} from "./schemas";
import { ImportOwnedPhoneNumberFormValues } from "./types";

export function ImportOwnedPhoneNumberDialog({
  onClose,
  hasActiveTFNumber = false,
}: {
  onClose: () => void;
  hasActiveTFNumber?: boolean;
}): ReactElement {
  const { subaccount } = useSubaccount()!;
  const { subscriberAudience } = useSubaccountAdminConfig()!;

  const [importOwnedPhoneNumber] = useMutation<
    ImportOwnedPhoneNumberMutation,
    ImportOwnedPhoneNumberMutationVariables
  >(IMPORT_OWNED_PHONE_NUMBER, {
    refetchQueries: PHONE_NUMBER_REFETCH_QUERIES,
    awaitRefetchQueries: true,
  });

  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useErrorMessageFormatter();
  const subaccountApolloClient = useNonCacheClearingApolloClient();
  const longCodeForOptInEnabled = useFeatureFlag("longCodeForOptIn");

  const [riskyMoveDetected, setRiskyMoveDetected] = useState(false);

  const {
    control,
    handleSubmit,
    formState,
    watch,
    setValue,
    setError,
    reset,
    trigger,
  } = useForm({
    resolver: zodResolver(ImportOwnedPhoneNumberFormSchema),
    defaultValues: {
      provider: ProviderName.Bandwidth,
      phoneNumber: "",
      messageClass: undefined,
      isOptInNumber: false,
      shortCodeConfig: subscriberAudience?.shortCodeConfig
        ? shortCodeConfigToFormValues(subscriberAudience.shortCodeConfig)
        : DEFAULT_SHORT_CODE_CONFIG_FORM_VALUES,
      tcrCampaignId: "",
      twilioAccountSid: "",
      allowRiskyMove: false,
      notifyTollFreeCreated: false,
      state: "NEW",
    },
    reValidateMode: "onBlur",
    mode: "onBlur",
  });

  async function onSubmit(
    values: ImportOwnedPhoneNumberFormValues
  ): Promise<void> {
    try {
      setRiskyMoveDetected(false);
      const response = await importOwnedPhoneNumber({
        variables: {
          input: {
            subaccountId: subaccount.id,
            provider: values.provider as ProviderName,
            phoneNumber: values.phoneNumber,
            messageClass: values.messageClass as PhoneNumberMessageClass,
            shortCodeConfig: values.isOptInNumber
              ? shortCodeConfigFormValuesToConfig(values.shortCodeConfig)
              : null,
            tcrCampaignId: values.tcrCampaignId || null,
            twilioAccountSid: values.twilioAccountSid || null,
            allowRiskyMove: values.allowRiskyMove,
            notifyTollFreeCreated: values.notifyTollFreeCreated,
            state:
              values.messageClass === PhoneNumberMessageClass.ShortCode
                ? values.state
                : undefined,
          },
        },
      });
      if (response.errors?.length) {
        if (
          response.errors.find(
            e => e.extensions?.code === "RISKY_PHONE_NUMBER_MOVE"
          )
        ) {
          setRiskyMoveDetected(true);
        }
        setError("root", { message: getErrorMessage(response.errors[0]) });
      } else {
        enqueueSnackbar("Phone number imported");
        await subaccountApolloClient.refetchQueries({
          include: [GET_SUBACCOUNT_ADMIN_FIELDS],
        });
        reset();
        onClose();
      }
    } catch (e) {
      setError("root", { message: getErrorMessage(e) });
    }
  }

  const providerValue = watch("provider");
  const messageClassValue = watch("messageClass");
  const isOptInNumberValue = watch("isOptInNumber");

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      switch (name) {
        case "messageClass":
          void trigger("phoneNumber");
          return;
        case "isOptInNumber":
        case "shortCodeConfig":
        case "shortCodeConfig.helpKeywords":
        case "shortCodeConfig.optInKeywords":
        case "shortCodeConfig.helpMessage":
        case "shortCodeConfig.optInMessage":
        case "shortCodeConfig.optOutKeywords":
        case "shortCodeConfig.optOutMessage":
          void trigger("shortCodeConfig");
          for (const shortCodeConfigKey of SHORT_CODE_CONFIG_KEYS) {
            void trigger(`shortCodeConfig.${shortCodeConfigKey}`);
          }
          return;
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  useEffect(() => {
    if (
      messageClassValue === PhoneNumberMessageClass.ShortCode &&
      !isOptInNumberValue
    ) {
      setValue("isOptInNumber", true);
    }
    if (
      messageClassValue === PhoneNumberMessageClass.Unregistered &&
      isOptInNumberValue
    ) {
      setValue("isOptInNumber", false);
    }
  }, [messageClassValue, isOptInNumberValue, setValue]);

  const lens = useLens({ control });
  const ShortCodeConfigLens = lens.focus("shortCodeConfig");

  const showOptionToNotify =
    !hasActiveTFNumber &&
    messageClassValue === "TOLL_FREE" &&
    providerValue &&
    ["FAKE", "BANDWIDTH"].includes(providerValue);

  const showOptInBox =
    longCodeForOptInEnabled &&
    PhoneNumberMessageClass.Unregistered !== messageClassValue;

  return (
    <Dialog open maxWidth="md" fullWidth>
      <DialogTitle>Import Phone Number</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ pt: 0 }}>
          <Typography>
            This will import a number into your subaccount.
          </Typography>

          <Box sx={{ pt: 1 }}>
            <RhfErrorBanner control={control} />
          </Box>
          <RhfSelect
            control={control}
            name="provider"
            label="Provider"
            fullWidth
          >
            {Object.values(ProviderName).map(provider => (
              <MenuItem key={provider} value={provider}>
                {provider}
              </MenuItem>
            ))}
          </RhfSelect>
          <RhfTextField
            control={control}
            name="phoneNumber"
            label="Phone Number"
            fullWidth
          />
          <RhfSelect
            control={control}
            name="messageClass"
            label="Message Class"
            fullWidth
          >
            {Object.values(PhoneNumberMessageClass).map(messageClass => (
              <MenuItem key={messageClass} value={messageClass}>
                <MessageClassDisplayName messageClass={messageClass} />
              </MenuItem>
            ))}
          </RhfSelect>
          {providerValue === ProviderName.Twilio && (
            <RhfTextField
              control={control}
              name="twilioAccountSid"
              label="Twilio Account SID"
              fullWidth
            />
          )}
          {messageClassValue === PhoneNumberMessageClass.TenDlc && (
            <RhfTextField
              control={control}
              name="tcrCampaignId"
              label="TCR Campaign ID (optional)"
              fullWidth
            />
          )}
          {showOptInBox && (
            <RhfCheckboxField
              control={control}
              name="isOptInNumber"
              label="Opt-in number"
              disabled={messageClassValue === PhoneNumberMessageClass.ShortCode}
            />
          )}
          {isOptInNumberValue && (
            <ShortCodeConfigForm
              lens={ShortCodeConfigLens}
              showSharedConfigWarning={!!subscriberAudience}
              showShortCodeWarning={
                messageClassValue === PhoneNumberMessageClass.ShortCode
              }
            />
          )}
          {messageClassValue === PhoneNumberMessageClass.ShortCode && (
            <RhfSelect control={control} name="state" label="Status" fullWidth>
              <MenuItem value="NEW">
                Pending (not usable by clients, can be tested by carriers)
              </MenuItem>
              <MenuItem value="ACTIVE">
                Active (approved by carriers and ready for client use)
              </MenuItem>
            </RhfSelect>
          )}
          {showOptionToNotify && (
            <RhfCheckboxField
              control={control}
              name="notifyTollFreeCreated"
              label="Would you like to notify the client (primary contact and subaccount owners) that toll-free is ready?"
            />
          )}

          {riskyMoveDetected && (
            <RhfCheckboxField
              control={control}
              name="allowRiskyMove"
              label="Allow risky phone number move?"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={formState.isSubmitting}
            onClick={() => {
              reset();
              onClose();
            }}
            color="secondary"
          >
            Cancel
          </Button>

          <SubmitButton
            disabled={formState.isSubmitting || !formState.isValid}
            color="primary"
            variant="contained"
            submitText="Import"
            loadingText="Importing..."
          />
        </DialogActions>
      </form>
    </Dialog>
  );
}
